import { useEffect, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { settingsApi, type Gs1Overview } from '../../services/settings';
import { WithWrite } from '../../lib/rbac';

function statusLabel(status: Gs1Overview['items'][number]['status']): string {
  return status === 'has_ean' ? 'Har EAN' : 'Saknar EAN';
}

function statusClass(status: Gs1Overview['items'][number]['status']): string {
  return status === 'has_ean' ? 'text-green-700 bg-green-50' : 'text-amber-800 bg-amber-50';
}

export function Gs1SettingsPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<Gs1Overview | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    companyPrefix: '',
    itemRefDigits: '3',
  });

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await settingsApi.getGs1Overview();
        if (active) {
          setOverview(data);
          setForm({
            companyPrefix: data.settings.companyPrefix,
            itemRefDigits: String(data.settings.itemRefDigits),
          });
        }
      } catch (error) {
        if (active) {
          toast.error(error instanceof Error ? error.message : 'Kunde inte hämta GS1-översikt');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.updateGs1Settings({
        companyPrefix: form.companyPrefix,
        itemRefDigits: Number(form.itemRefDigits),
      });
      toast.success('GS1-inställningar sparade');
      const data = await settingsApi.getGs1Overview();
      setOverview(data);
      setForm({
        companyPrefix: data.settings.companyPrefix,
        itemRefDigits: String(data.settings.itemRefDigits),
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara GS1-inställningar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold">GS1</h2>
      <p className="text-sm text-gray-600">
        Översikt över produktregister och streckkoder inför GS1-arbetet. ApisAI allokerar nästa lediga GTIN-13 automatiskt när en produkt markeras för GS1.
      </p>

      {loading && !overview ? <p className="text-sm text-gray-500">Laddar översikt...</p> : null}

      {overview ? (
        <>
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
            <section className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
              <div className="font-medium">GS1-inställningar</div>
              <label className="block text-sm font-medium">Företagsprefix</label>
              <input
                className="w-full"
                value={form.companyPrefix}
                onChange={(event) => setForm((current) => ({ ...current, companyPrefix: event.target.value }))}
              />
              <label className="block text-sm font-medium">Löpnummer-siffror</label>
              <input
                type="number"
                min={1}
                max={6}
                className="w-full"
                value={form.itemRefDigits}
                onChange={(event) => setForm((current) => ({ ...current, itemRefDigits: event.target.value }))}
              />
              <div className="text-xs text-gray-500">
                Företagsprefix + löpnummer-siffror ska tillsammans bli 12 siffror före kontrollsiffra.
              </div>
              <WithWrite moduleCode="settings">
                <button type="button" className="btn btn-success px-2 py-1" onClick={handleSave} disabled={saving}>
                  {saving ? 'Sparar...' : 'Spara'}
                </button>
              </WithWrite>
            </section>

            <section className="rounded border border-gray-200 bg-white p-3 space-y-3">
              <div className="font-medium">Nästa lediga GTIN-13</div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                <div className="rounded border border-gray-200 bg-gray-50 p-2">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Prefix</div>
                  <div className="text-lg font-semibold">{overview.settings.companyPrefix}</div>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 p-2">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Pool</div>
                  <div className="text-lg font-semibold">{overview.totals.barcodePoolPlanned}</div>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 p-2">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Lediga nu</div>
                  <div className="text-lg font-semibold">{overview.totals.availableNow}</div>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 p-2">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Nästa GTIN</div>
                  <div className="text-lg font-semibold">{overview.nextAvailable[0]?.gtin13 ?? '-'}</div>
                </div>
              </div>
              <div className="rounded border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Löpnummer</th>
                      <th className="px-2 py-1.5 text-left">GTIN-13</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.nextAvailable.map((item) => (
                      <tr key={item.gtin13} className="border-t border-gray-100">
                        <td className="px-2 py-1.5">{item.itemRef}</td>
                        <td className="px-2 py-1.5 font-medium">{item.gtin13}</td>
                      </tr>
                    ))}
                    {overview.nextAvailable.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="px-2 py-4 text-sm text-gray-500">Inga lediga GTIN hittades i nuvarande pool.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
            <div className="text-sm">
              <span className="text-gray-500">GS1 Developer:</span>{' '}
              <a href={overview.developerUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                {overview.developerUrl}
              </a>
            </div>
            <div className="text-sm"><span className="text-gray-500">Planerad kodpool:</span> {overview.totals.barcodePoolPlanned} streckkoder</div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <div className="rounded border border-gray-200 bg-white p-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Produkter</div>
                <div className="text-lg font-semibold">{overview.totals.products}</div>
              </div>
              <div className="rounded border border-gray-200 bg-white p-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Har EAN</div>
                <div className="text-lg font-semibold">{overview.totals.withEan}</div>
              </div>
              <div className="rounded border border-gray-200 bg-white p-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Saknar EAN</div>
                <div className="text-lg font-semibold">{overview.totals.missingEan}</div>
              </div>
              <div className="rounded border border-gray-200 bg-white p-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Säljbara utan EAN</div>
                <div className="text-lg font-semibold">{overview.totals.sellableMissingEan}</div>
              </div>
            </div>
          </div>

          <div className="rounded border border-gray-200 overflow-hidden">
            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 18rem)' }}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Status</th>
                    <th className="px-2 py-1.5 text-left">Artikelnummer</th>
                    <th className="px-2 py-1.5 text-left">EAN</th>
                    <th className="px-2 py-1.5 text-left">Namn</th>
                    <th className="px-2 py-1.5 text-left">Kategori</th>
                    <th className="px-2 py-1.5 text-left">Typ</th>
                    <th className="px-2 py-1.5 text-left">Enhet</th>
                    <th className="px-2 py-1.5 text-left">Säljbar</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.items.map((item) => (
                    <tr key={item.productId} className="border-t border-gray-100">
                      <td className="px-2 py-1.5">
                        <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${statusClass(item.status)}`}>
                          {statusLabel(item.status)}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">{item.sku ?? '-'}</td>
                      <td className="px-2 py-1.5">{item.ean ?? '-'}</td>
                      <td className="px-2 py-1.5">{item.name}</td>
                      <td className="px-2 py-1.5">{item.categoryLabel ?? '-'}</td>
                      <td className="px-2 py-1.5">{item.kind}</td>
                      <td className="px-2 py-1.5">{item.uomLabel ?? '-'}</td>
                      <td className="px-2 py-1.5">{item.canSell ? 'Ja' : 'Nej'}</td>
                    </tr>
                  ))}
                  {overview.items.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-2 py-4 text-sm text-gray-500">Inga produkter hittades.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
